import { Envelope } from '../../../../design-system/icons';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/utils';
import { BLOCKER_LABELS, sendableTargets, targetBlocker, visibleTargets, type CreatorFilter, type WizardAction, type WizardState } from './wizardState';
import { WizardStepHeader } from './WizardStepHeader';

const chips: ReadonlyArray<{ key: CreatorFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'has_email', label: 'Has email' },
  { key: 'no_email', label: 'No email' },
];

export const NO_EMAIL_YET_COPY = 'No email yet — the draft waits for one'; // copy: ours

/** Step 1: who this send is for. Creators already queued or sent are shown blocked; one with no email can still be picked. */
export function CreatorsStep({ state, dispatch }: { state: WizardState; dispatch: (action: WizardAction) => void }) {
  const shown = visibleTargets(state);
  const selectable = shown.filter((target) => targetBlocker(target) === null);
  const selected = sendableTargets(state).length;
  const blocked = state.targets.filter((target) => targetBlocker(target) !== null).length;
  const sub = blocked > 0 ? `${selected} selected · ${blocked} can't be in this send` : `${selected} selected`;

  return (
    <div className="mx-auto w-full max-w-[880px] space-y-5">
      <WizardStepHeader heading="Creators" sub={sub} />
      <div className="space-y-3">
        <Input aria-label="Search creators" placeholder="Search creators" className="h-10 rounded-[10px]" value={state.search} onChange={(event) => dispatch({ type: 'search', q: event.target.value })} />
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button key={chip.key} type="button" aria-pressed={state.filter === chip.key} onClick={() => dispatch({ type: 'filter', filter: chip.key })}
              className={cn('h-7 shrink-0 rounded-full px-3 text-body-sm-medium', state.filter === chip.key ? 'bg-foreground text-background' : 'bg-muted text-foreground')}>
              {chip.label}
            </button>
          ))}
          <button type="button" className="ml-auto h-7 text-body-sm-medium text-foreground hover:underline" onClick={() => dispatch({ type: 'selectVisible', entryIds: selectable.map((target) => target.entryId) })}>Select all shown</button>
        </div>
      </div>
      <ul className="space-y-2">
        {shown.map((target) => {
          const blocker = targetBlocker(target);
          const noEmail = !blocker && !target.unverified && !target.hasContact;
          return (
            <li key={target.entryId} className={cn('flex min-h-[62px] items-center gap-3 rounded-lg border border-border bg-card px-3 py-2', blocker && 'opacity-60')}>
              <Checkbox
                aria-label={`Select ${target.username}`}
                checked={!blocker && state.selected.includes(target.entryId)}
                disabled={!!blocker}
                onCheckedChange={() => dispatch({ type: 'toggle', entryId: target.entryId })}
              />
              {target.avatarUrl ? <img src={target.avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" /> : <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-caption-regular text-muted-foreground">{(target.displayName ?? target.username).slice(0, 1).toUpperCase()}</span>}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-sm-medium text-foreground">{target.displayName ?? `@${target.username}`}</span>
                <span className={cn('block truncate text-xs', noEmail ? 'text-amber-700' : 'text-muted-foreground')}>
                  {blocker ? BLOCKER_LABELS[blocker] : target.unverified ? 'Confirmed when sent' : noEmail ? NO_EMAIL_YET_COPY : target.email}
                </span>
              </span>
              <Envelope size={20} aria-hidden="true" className={cn('shrink-0', target.hasContact || target.unverified ? 'text-foreground' : 'text-muted-foreground opacity-40')} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
